/**
 * Client HTTP du back-office de plateforme.
 *
 * Le jeton d'acces vit en memoire, jamais dans le stockage local : une page
 * qui ouvre et ferme des restaurants ne laisse pas trainer ses identifiants.
 * La session se restaure par le cookie de rafraichissement.
 */

import type { ApiErrorBody, ErrorCode } from '@scan2eat/shared';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    public readonly code: ErrorCode,
    public readonly httpStatus: number,
    message: string,
    public readonly details: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Le back-office de plateforme a son propre cookie de rafraichissement.
 *
 * Les deux back-offices parlent a la meme API, donc au meme hote : avec un nom
 * unique, se connecter ici ecrasait la session du back-office restaurant, et
 * le rechargement suivant renvoyait l'un des deux a l'ecran de connexion.
 * Voir docs/07, section 1.3, et l'ADR 0015.
 */
export const AUTH_SCOPE = '?scope=platform';

async function refresh(): Promise<string> {
  const res = await fetch(`${BASE_URL}/admin/auth/refresh${AUTH_SCOPE}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new ApiError('TOKEN_EXPIRED', 401, 'Rafraichissement echoue');

  const data = (await res.json()) as { accessToken: string };
  accessToken = data.accessToken;
  return data.accessToken;
}

/** Une seule tentative de rafraichissement a la fois, partagee. */
function doRefresh(): Promise<string> {
  refreshPromise ??= refresh().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as ApiErrorBody;
    return new ApiError(body.error.code, res.status, body.error.message, body.error.details);
  } catch {
    return new ApiError('INTERNAL_ERROR', res.status, `Erreur HTTP ${res.status}`);
  }
}

/**
 * Faut-il tenter un rafraichissement apres un 401 sur cette route ?
 *
 * Non pour les routes d'authentification elles-memes : un mot de passe errone
 * rend 401, et y repondre par un rafraichissement puis une redirection ferait
 * boucler la page de connexion sur elle-meme.
 */
function shouldAttemptRefresh(path: string): boolean {
  return !path.startsWith('/admin/auth/login') && !path.startsWith('/admin/auth/refresh');
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  let res = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' });

  // Le rafraichissement ne depend pas du jeton en memoire, mais du cookie.
  // Exiger un jeton ici privait de session tout rechargement de page : le jeton
  // ne survit pas a un F5, alors que le cookie, lui, est toujours la.
  if (res.status === 401 && shouldAttemptRefresh(path)) {
    try {
      headers.set('Authorization', `Bearer ${await doRefresh()}`);
      res = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
    } catch {
      accessToken = null;
      // Ne pas rediriger si l'on y est deja : sans cette garde, une page de
      // connexion sans cookie se rechargerait indefiniment.
      if (window.location.pathname !== '/connexion') {
        window.location.href = '/connexion';
      }
      throw new ApiError('TOKEN_EXPIRED', 401, 'Session expiree');
    }
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}
